import {
  addToCart,
  removeFromCart,
  getCart,
  createCart,
  updateCart,
} from '@/clients/shopify/client'
import type { CartLineInput, CartItem } from '@/types/shopify'
import { getGiftData } from '@/clients/sanity/queries'
import type { CMSGwmPromotions } from '@/types/sanity'

import { $cart } from './stores'

let isUpdatingCartWithGift = false

// Helper to validate cart object
const validateCart = (cart: any): boolean => {
  if (!cart || !cart.id || !Array.isArray(cart.lines)) {
    console.error('Invalid cart object:', cart)
    return false
  }
  return true
}

// Helper to manage removed gift state in localStorage
const getRemovedGiftTiers = (): string[] => {
  const removed = localStorage.getItem('removedGiftTiers')
  return removed ? JSON.parse(removed) : []
}

const addRemovedGiftTier = (tierAmount: string) => {
  const removedTiers = getRemovedGiftTiers()
  if (!removedTiers.includes(tierAmount)) {
    removedTiers.push(tierAmount)
    localStorage.setItem('removedGiftTiers', JSON.stringify(removedTiers))
  }
}

const clearRemovedGiftTiers = () => {
  localStorage.removeItem('removedGiftTiers')
}

export const addToCartOperation = async (
  cartId: string,
  lines: {
    merchandiseId: string
    quantity: number
    sellingPlanId?: string
    attributes?: { key: string; value: string }[]
  }[],
) => {
  try {
    clearRemovedGiftTiers()
    let updatedCart = await addToCart(cartId, lines)
    if (!validateCart(updatedCart))
      throw new Error('Invalid cart after addToCart')
    updatedCart = await updateCartWithBestGift(updatedCart)
    $cart.set(updatedCart)
    return updatedCart
  } catch (error) {
    console.error('Error in addToCartOperation:', error)
    throw error
  }
}

export const updateToCartOperation = async (
  cartId: string,
  lines: CartLineInput[],
) => {
  try {
    clearRemovedGiftTiers()
    const cartBeforeUpdate = await getCart(cartId)
    if (!validateCart(cartBeforeUpdate))
      throw new Error('Invalid cart before update')
    const giftItemsBefore = cartBeforeUpdate?.lines.filter(
      (line: CartItem) =>
        line.attributes?.find(
          (attr) => attr.key === '_gift_type' && attr.value === 'amount',
        ),
    )

    let updatedCart = await updateCart(cartId, lines)
    if (!validateCart(updatedCart))
      throw new Error('Invalid cart after updateCart')

    const giftItemsAfter = updatedCart.lines.filter(
      (line: CartItem) =>
        line.attributes?.find(
          (attr) => attr.key === '_gift_type' && attr.value === 'amount',
        ),
    )

    giftItemsBefore?.forEach((giftItem: CartItem) => {
      const tierAmount = giftItem.attributes.find(
        (attr) => attr.key === '_gift_tier_amount',
      )?.value
      const isGiftRemoved =
        !giftItemsAfter.some((line) => line.id === giftItem.id) ||
        lines.some(
          (line) =>
            line.id === giftItem.id &&
            (line.quantity === 0 || line.quantity === undefined),
        )

      if (isGiftRemoved && tierAmount) {
        addRemovedGiftTier(tierAmount)
      }
    })

    updatedCart = await updateCartWithBestGift(updatedCart)
    $cart.set(updatedCart)
    return updatedCart
  } catch (error) {
    console.error('Error in updateToCartOperation:', error)
    throw error
  }
}

export const removeFromCartOperation = async (
  cartId: string,
  lineIds: string[],
) => {
  try {
    const cartBeforeRemoval = await getCart(cartId)
    if (!validateCart(cartBeforeRemoval))
      throw new Error('Invalid cart before removal')

    const removedGiftItems = cartBeforeRemoval?.lines.filter(
      (line: CartItem) =>
        lineIds.includes(line.id) &&
        line.attributes?.find(
          (attr) => attr.key === '_gift_type' && attr.value === 'amount',
        ),
    )

    removedGiftItems?.forEach((item: CartItem) => {
      const tierAmount = item.attributes.find(
        (attr) => attr.key === '_gift_tier_amount',
      )?.value
      if (tierAmount) {
        addRemovedGiftTier(tierAmount)
      }
    })

    let updatedCart = await removeFromCart(cartId, lineIds)
    if (!validateCart(updatedCart))
      throw new Error('Invalid cart after removeFromCart')

    updatedCart = await updateCartWithBestGift(updatedCart)
    $cart.set(updatedCart)
    return updatedCart
  } catch (error) {
    console.error('Error in removeFromCartOperation:', error)
    throw error
  }
}

export const getCartOperation = async (cartId: string) => {
  try {
    const cart = await getCart(cartId)
    $cart.set(cart)
    return cart
  } catch (error) {
    console.error('Error in getCartOperation:', error)
    throw error
  }
}

export const createCartOperation = async () => {
  try {
    const cart = await createCart()
    if (!validateCart(cart)) throw new Error('Invalid cart from createCart')
    clearRemovedGiftTiers() // Reset removed gifts for new cart
    $cart.set(cart)
    return cart
  } catch (error) {
    console.error('Error in createCartOperation:', error)
    throw error
  }
}
const updateCartWithBestGift = async (cart: any) => {
  if (isUpdatingCartWithGift) return cart
  if (!validateCart(cart)) {
    throw new Error(
      'Unable to retrieve line items from cart in updateCartWithBestGift',
    )
  }

  isUpdatingCartWithGift = true
  try {
    const allGifts = await getGiftData()
    const amountGifts = Array.isArray(allGifts)
      ? allGifts.filter(
          (gift: CMSGwmPromotions) =>
            gift.type === 'amount' && gift.statusActive,
        )
      : []

    const giftItemsInCart = cart.lines.filter(
      (line: CartItem) =>
        line.attributes?.find(
          (attr) => attr.key === '_gift_type' && attr.value === 'amount',
        ),
    )

    const mealsInCart = cart.lines.filter(
      (line: CartItem) =>
        line.merchandise.product.productType !== 'Clean Boutique',
    )

    const mealsTotal = mealsInCart.reduce(
      (total: number, line: CartItem) =>
        total + parseFloat(line.cost.totalAmount.amount || '0'),
      0,
    )

    const total = giftItemsInCart.length
      ? parseFloat(cart.cost.subtotalAmount.amount || '0') -
        parseFloat(giftItemsInCart[0].cost.totalAmount.amount || '0') -
        mealsTotal
      : parseFloat(cart.cost.subtotalAmount.amount || '0') - mealsTotal

    let allTiers: { gift: CMSGwmPromotions; tier: any }[] = []
    for (const gift of amountGifts) {
      gift.tiers.forEach((tier: any) => {
        allTiers.push({ gift, tier })
      })
    }

    const removedGiftTiers = getRemovedGiftTiers()

    let updatedCart = cart

    if (removedGiftTiers.length > 0) {
      if (giftItemsInCart.length) {
        updatedCart = await removeFromCart(
          cart.id,
          giftItemsInCart.map((item: CartItem) => item.id),
        )
        if (!validateCart(updatedCart)) {
          throw new Error('Invalid cart after removing existing gift items')
        }
      }
      return updatedCart
    }

    const eligibleTiers = allTiers
      .filter((entry) => entry.tier.amount <= total)
      .sort((a, b) => b.tier.amount - a.tier.amount)

    const bestTierEntry = eligibleTiers[0]

    if (bestTierEntry) {
      const bestGift = bestTierEntry.gift
      const bestTier = bestTierEntry.tier

      if (
        giftItemsInCart.length &&
        !giftItemsInCart.some(
          (item: CartItem) =>
            item.attributes.find((attr) => attr.key === '_gift_tier_amount')
              ?.value === bestTier.amount.toString(),
        )
      ) {
        updatedCart = await removeFromCart(
          cart.id,
          giftItemsInCart.map((item: CartItem) => item.id),
        )
        if (!validateCart(updatedCart)) {
          throw new Error('Invalid cart after removing gift items')
        }
      }

      if (
        !giftItemsInCart.some(
          (item: CartItem) =>
            item.attributes.find((attr) => attr.key === '_gift_tier_amount')
              ?.value === bestTier.amount.toString(),
        )
      ) {
        const tagAttributes =
          bestGift.productTags?.map(
            (tag: { label: string; theme: string }) => ({
              key: `_gift_tag_${tag.label.toLowerCase().replace(/\s+/g, '_')}`,
              value: tag.theme || 'default',
            }),
          ) || []
        const giftLineItem = {
          merchandiseId: bestTier.giftProductVariant.store.gid,
          quantity: 1,
          attributes: [
            { key: '_gift', value: 'true' },
            { key: '_gift_type', value: 'amount' },
            { key: '_gift_tier_amount', value: bestTier.amount.toString() },
            ...tagAttributes,
          ],
        }

        updatedCart = await addToCart(updatedCart.id, [giftLineItem])
        if (!updatedCart) {
          throw new Error('addToCart returned null or undefined')
        }
        if (!validateCart(updatedCart)) {
          throw new Error('Invalid cart after adding gift item')
        }
      }
    } else if (giftItemsInCart.length) {
      updatedCart = await removeFromCart(
        cart.id,
        giftItemsInCart.map((item: CartItem) => item.id),
      )
      if (!validateCart(updatedCart)) {
        throw new Error('Invalid cart after removing ineligible gift items')
      }
    }

    return updatedCart
  } catch (error) {
    console.error('Error in updateCartWithBestGift:', error)
    throw error
  } finally {
    isUpdatingCartWithGift = false
  }
}
